import moment from 'moment';
import { isFutureDateInPST } from 'utilities/pstDateUtils';
import { handleError } from 'utilities/utilities';
import {
  AccountInfoRequestType,
  AccountInfoType,
  AllOrdersItemType,
  AllOrdersRequestType,
  AssignCplRequestModel,
  AssignCplResponseModel,
  AssignJacketRequest,
  AssignJacketResponse,
  BookedOrderType,
  BookOrderRequestType,
  CplTypeResponseModel,
  CreateFileRequestModel,
  CreateFileResponseModel,
  CreateInvoicedFileRequestModel,
  CreateInvoicedFileResponseModel,
  CreditMemoRequestModel,
  CreditMemoResponseModel,
  FileInProcessType,
  FileMisc,
  FileNrop,
  FileType,
  FirmSearchRequestType,
  FirmSearchResponseType,
  GetCplOnlyRemittedFileResponseModel,
  GetInvoiceDetailsResponseModel,
  InvoiceSearchRequestModel,
  InvoiceSearchResult,
  LockboxNumberType,
  MergePdfsRequestModel,
  MergePdfsResponseModel,
  NotesDocumentDataType,
  NotesType,
  OrderFileDetailsType,
  OrderFileQueueResponseType,
  OrderFilesRequestType,
  OrderRequestType,
  OrderType,
  PolicyInventoryType,
  RemittanceReceivableNotesResponseModel,
  RemittanceReceivableResponseModel,
  RemittanceReceivableSearchRequestModel,
  RemittanceReceivableUpdateRequestModel,
  SearchFirmByAccountResponse,
  UploadNoteDocumentRequestType,
  UploadNotesAndDocumentsRequestType,
  UploadNotesAndDocumentsResponseType,
  UploadRemittanceReceivableNotesRequestModel,
  UserDetailType,
  UserType,
} from '../../features/orderManagement/types';
import { getAgencyApiHost, getApiHost } from '../host-by-env';

export async function firmSearchApi(
  payload: FirmSearchRequestType,
  token: string,
): Promise<{ result: FirmSearchResponseType; errorCode?: string | number }> {
  const url = ` ${getApiHost()}order-management/firm-search`;
  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  return await response.json();
}

export async function getLockboxNumberApi(token: string): Promise<Array<LockboxNumberType>> {
  const url = `${getApiHost()}order-management/lockbox-items`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  for (const item of json.result) {
    item.name = item.display;
    item.value = item.lockboxId;
  }
  return json.result;
}

export async function searchFirmsByAccount(
  accountNumber: number,
  token: string,
): Promise<Array<SearchFirmByAccountResponse>> {
  const url = `${getApiHost()}firms/search/account/${accountNumber}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  return json.result;
}

export async function getFilesApi(
  firmId: number,
  token: string,
  fileInfoStatus?: string,
): Promise<{ result: Array<FileType>; errorCode?: string | number }> {
  let url = `${getApiHost()}order-management/files/${firmId}`;
  if (fileInfoStatus) {
    url += `?fileInfoStatus=${encodeURIComponent(fileInfoStatus)}`;
  }
  const response = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  return await response.json();
}

export async function getAccountInfoApi(payload: AccountInfoRequestType, token: string): Promise<AccountInfoType[]> {
  const url = `${getApiHost()}order-management/account-info`;
  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  return json.result;
}

export async function bookOrderApi(payload: BookOrderRequestType, token: string): Promise<BookedOrderType> {
  const url = `${getApiHost()}order-management/book-order`;
  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function createOrderApi(payload: OrderRequestType, token: string): Promise<OrderType> {
  if (isFutureDateInPST(payload.orderDate)) {
    throw new Error('Order Date cannot be a future date');
  }
  payload.orderDate = moment(payload.orderDate, 'MM/DD/YYYY').format('YYYY-MM-DD');
  const url = `${getApiHost()}order-management/order`;
  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function updateOrderApi(payload: OrderRequestType, token: string): Promise<OrderType> {
  if (isFutureDateInPST(payload.orderDate)) {
    throw new Error('Order Date cannot be a future date');
  }
  payload.orderDate = moment(payload.orderDate, 'MM/DD/YYYY').format('YYYY-MM-DD');
  const url = `${getApiHost()}order-management/order`;
  const response = await fetch(url, {
    method: 'PUT',
    body: JSON.stringify(payload),
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function enqueueFilesApi(
  orderId: number,
  payload: OrderFilesRequestType,
  token: string,
): Promise<OrderFileQueueResponseType> {
  const url = `${getApiHost()}order-management/order/${orderId}/enqueue-files`;
  const response = await fetch(url, {
    method: 'PUT',
    body: JSON.stringify(payload),
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  return await response.json();
}

export async function dequeueFilesApi(
  orderId: number,
  payload: OrderFilesRequestType,
  token: string,
): Promise<OrderFileQueueResponseType> {
  const url = `${getApiHost()}order-management/order/${orderId}/dequeue-files`;
  const response = await fetch(url, {
    method: 'PUT',
    body: JSON.stringify(payload),
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  return await response.json();
}

export async function getFilesInProcessApi(
  token: string,
): Promise<{ result: Array<FileInProcessType>; errorCode?: string | number }> {
  const url = `${getApiHost()}order-management/files-in-process`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  return await response.json();
}

export async function getAllOrdersApi(payload: AllOrdersRequestType, token: string): Promise<Array<AllOrdersItemType>> {
  const params = new URLSearchParams();
  if (payload.lockboxId) {
    params.append('lockboxId', String(payload.lockboxId));
  }
  if (payload.fileNumber) {
    params.append('fileNumber', encodeURIComponent(payload.fileNumber));
  }
  if (payload.policyNumber) {
    params.append('policyNumber', encodeURIComponent(payload.policyNumber));
  }
  if (payload.accountNumber) {
    params.append('accountNumber', String(payload.accountNumber));
  }
  if (payload.orderId) {
    params.append('orderId', String(payload.orderId));
  }
  if (payload.orderNumber) {
    params.append('orderNumber', payload.orderNumber);
  }
  if (payload.orderStatus !== undefined && payload.orderStatus !== null) {
    params.append('orderStatus', String(payload.orderStatus));
  }
  if (payload.stateCode) {
    params.append('stateCode', encodeURIComponent(payload.stateCode));
  }
  if (payload.invoiceNumber) {
    params.append('invoiceNumber', payload.invoiceNumber);
  }
  if (payload.batchReferenceNumber) {
    params.append('batchReferenceNumber', encodeURIComponent(payload.batchReferenceNumber));
  }
  const query = params.size > 0 ? `?${params.toString()}` : '';
  payload.fromOrderDate = moment(payload.fromOrderDate, 'MM/DD/YYYY').format('YYYY-MM-DD');
  payload.toOrderDate = moment(payload.toOrderDate, 'MM/DD/YYYY').format('YYYY-MM-DD');
  const url = `${getApiHost()}order-management/all-orders/${payload.fromOrderDate}/${payload.toOrderDate}${query}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function cancelOrderApi(
  orderId: number,
  token: string,
): Promise<{ result: { orderCancelStatus: boolean } }> {
  const url = `${getApiHost()}order-management/order/${orderId}/cancel`;

  const response = await fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });

  const json = await response.json();
  handleError(json);
  return json;
}

export async function getOrderFileDetailsApi(orderId: number, token: string): Promise<OrderFileDetailsType> {
  const url = `${getApiHost()}order-management/order/${orderId}/files`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function getNotesApi(orderId: number, fileId: string, token: string): Promise<Array<NotesType>> {
  const url = `${getApiHost()}order-management/notes/${orderId}/${fileId}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  // sort json.result by createdDate in ascending order
  json.result.sort(
    (a: NotesType, b: NotesType) => new Date(a.createdDate ?? '').getTime() - new Date(b.createdDate ?? '').getTime(),
  );
  return json.result;
}

export async function getDocumentContentApi(documentName: string, token: string): Promise<NotesDocumentDataType> {
  const url = `${getAgencyApiHost()}ordermanagement-service/v1/remitbatchdocument?fileName=${encodeURIComponent(
    documentName,
  )}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return { documentData: json.Result };
}

export async function uploadNotesAndDocumentsApi(
  payload: UploadNotesAndDocumentsRequestType,
  token: string,
): Promise<UploadNotesAndDocumentsResponseType> {
  const url = `${getApiHost()}order-management/notes`;
  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function uploadNoteDocumentApi(
  payload: UploadNoteDocumentRequestType,
  token: string,
): Promise<UploadNotesAndDocumentsResponseType> {
  const url = `${getAgencyApiHost()}ordermanagement-service/v1/remitbatchnotes`;
  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    if (response.status === 413) {
      // Handle the 413 error specifically
      throw Error(`The file you're trying to upload is too large. Please reduce the file size and try again.`);
    }
  }
  const json = await response.json();
  handleError(json);
  return { ...json.Result, message: json.Result?.Message };
}

export async function getUser(userId: number, token: string): Promise<UserType> {
  const url = `${getApiHost()}order-management/user/${userId}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function setUser(payload: UserType, token: string): Promise<UserType> {
  const url = `${getApiHost()}order-management/user`;
  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function getTeamUsers(fscRoleId: number, token: string): Promise<Array<UserDetailType>> {
  const url = `${getApiHost()}order-management/users/${fscRoleId}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function getPolicyInventoryApi(accoutNumber: string, token: string): Promise<PolicyInventoryType[]> {
  const url = `${getApiHost()}order-management/policy-inventory/${accoutNumber}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function createFileApi(request: CreateFileRequestModel, token: string): Promise<CreateFileResponseModel> {
  const url = `${getApiHost()}order-management/create-file`;
  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(request),
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function createInvoicedFileApi(
  request: CreateInvoicedFileRequestModel,
  token: string,
): Promise<CreateInvoicedFileResponseModel> {
  const url = `${getApiHost()}order-management/create-invoiced-file`;
  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(request),
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function getCplTypesApi(accoutNumber: string, token: string): Promise<CplTypeResponseModel[]> {
  const url = `${getApiHost()}order-management/cpl/${accoutNumber}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function assignCplApi(request: AssignCplRequestModel, token: string): Promise<AssignCplResponseModel> {
  const url = `${getApiHost()}order-management/cpl`;
  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(request),
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function assignPolicyApi(request: AssignJacketRequest, token: string): Promise<AssignJacketResponse> {
  const url = `${getApiHost()}order-management/assign-policy`;
  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(request),
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function getRemittanceReceivables(
  token: string,
  searchRequest: RemittanceReceivableSearchRequestModel,
): Promise<RemittanceReceivableResponseModel[]> {
  const params = new URLSearchParams();
  if (searchRequest.startDate) params.append('startDate', searchRequest.startDate);
  if (searchRequest.endDate) params.append('endDate', searchRequest.endDate);
  if (searchRequest.lockboxId !== undefined) params.append('lockboxId', searchRequest.lockboxId.toString());
  if (searchRequest.checkNumber) params.append('checkNumber', searchRequest.checkNumber);
  if (searchRequest.categoryCdId !== undefined) params.append('categoryCdId', searchRequest.categoryCdId.toString());
  if (searchRequest.entityName) params.append('entityName', searchRequest.entityName);
  if (searchRequest.statusCdId !== undefined) params.append('statusCdId', searchRequest.statusCdId.toString());

  const url = `${getApiHost()}order-management/remittance-receivables?${params.toString()}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function updateRemittanceReceivableAssignments(
  request: RemittanceReceivableUpdateRequestModel,
  token: string,
): Promise<boolean> {
  const url = `${getApiHost()}order-management/remittance-receivables/assignment`;
  const response = await fetch(url, {
    method: 'PUT',
    body: JSON.stringify(request),
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function mergePdfs(request: MergePdfsRequestModel, token: string): Promise<MergePdfsResponseModel> {
  const url = `${getApiHost()}order-management/merge-pdfs`;
  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(request),
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function getFileNropsByFileId(token: string, fileId: number): Promise<FileNrop[]> {
  const url = `${getApiHost()}order-management/file/nrop/${fileId}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function updateFileNrop(token: string, request: FileNrop): Promise<boolean> {
  const url = `${getApiHost()}order-management/file/nrop`;
  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(request),
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function deleteFileNrop(token: string, fileId?: number, fileNropId?: number): Promise<boolean> {
  const url = `${getApiHost()}order-management/file/nrop?fileId=${fileId}&fileNropId=${fileNropId}`;
  const response = await fetch(url, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function getFileMiscByFileId(token: string, fileId: number): Promise<FileMisc> {
  const url = `${getApiHost()}order-management/file/misc/${fileId}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function updateFileMisc(token: string, request: FileMisc): Promise<boolean> {
  const url = `${getApiHost()}order-management/file/misc`;
  const response = await fetch(url, {
    method: 'PUT',
    body: JSON.stringify(request),
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function getRemittanceReceivableNotes(
  token: string,
  remittanceReceivableId: number,
): Promise<RemittanceReceivableNotesResponseModel[]> {
  const url = `${getApiHost()}order-management/remittance-receivables/${remittanceReceivableId}/notes`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function uploadRemittanceReceivableNote(
  token: string,
  request: UploadRemittanceReceivableNotesRequestModel,
): Promise<void> {
  const url = `${getApiHost()}order-management/remittance-receivables/notes`;
  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(request),
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function getInvoicesApi(
  payload: InvoiceSearchRequestModel,
  token: string,
): Promise<Array<InvoiceSearchResult>> {
  const url = `${getApiHost()}remittance/search-invoice`;
  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function getInvoiceDetailsApi(
  token: string,
  invoiceNumber: string,
  accountNumber: number,
): Promise<GetInvoiceDetailsResponseModel> {
  const url = `${getApiHost()}remittance/invoice-details?invoiceNumber=${encodeURIComponent(
    invoiceNumber,
  )}&accountNumber=${encodeURIComponent(accountNumber)}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function getCplOnlyRemittedFileEligibilityApi(
  token: string,
  fileId: number,
): Promise<GetCplOnlyRemittedFileResponseModel> {
  const url = `${getApiHost()}remittance/cpl-only-remitted-file?fileId=${fileId}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function createCreditMemoApi(
  payload: CreditMemoRequestModel,
  token: string,
): Promise<CreditMemoResponseModel> {
  const url = `${getApiHost()}remittance/credit-memo`;
  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}
